/**
 * 資料ダウンロード／モックアップ相談／チェックリスト ページ Script
 *
 * - Pickles フォームの検証・送信ロジックを集約（テンプレ側はマークアップのみ）。
 *   フォーム固有の値（data-form-key）は DOM から読み取り、複数フォームに対応。
 * - FONTPLUS をフォーム要素へ再適用。
 * - ロード時フェードイン（コンタクトと同じ gsap 設定）。EN見出し非表示のため
 *   JA見出しを pageTitleIntro の遅延制御から外し、l-section と同時にフェードさせる。
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const PF_ENDPOINT_BASE =
  'https://pickles.internal.spice-factory.co.jp/api/public/forms/';
// 送信成功後の遷移先（完了ページ）。全環境で効くよう相対パスで指定。
const PF_COMPLETE_URL = '/contact/complete/';

// ---------------------------------------------------------------------------
// JA見出しを pageTitleIntro に拾わせない（footer スクリプトのためヘッダーはパース済み）。
// 初期 opacity:0 にして、l-section と同時にフェードインさせる。
// ---------------------------------------------------------------------------
const titleJa = document.querySelector('.p-document-dl .js-page-title-ja');
if (titleJa) {
  titleJa.classList.remove('js-page-title-ja');
  gsap.set(titleJa, { opacity: REDUCED ? 1 : 0 });
}

// ---------------------------------------------------------------------------
// FONTPLUS 再適用（フォーム要素／入力文字に Web フォントを当てる）
// ---------------------------------------------------------------------------
function fpReload() {
  if (window.FONTPLUS && typeof window.FONTPLUS.reload === 'function') {
    window.FONTPLUS.reload();
  }
}

// ---------------------------------------------------------------------------
// Pickles フォーム
// ---------------------------------------------------------------------------
function pfUuid() {
  try {
    return crypto.randomUUID();
  } catch (e) {
    return 'pf-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }
}

function pfTracking() {
  const p = new URLSearchParams(location.search);
  const t = {
    current_url: location.href,
    current_path: location.pathname,
    referrer: document.referrer || '',
  };
  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'wbraid',
    'gbraid',
    'fbclid',
  ].forEach(k => {
    const v = p.get(k);
    if (v) t[k] = v;
  });
  try {
    const KEY = 'pickles_first_touch';
    const ex = localStorage.getItem(KEY);
    if (ex) {
      const ft = JSON.parse(ex);
      t.first_landing_url = ft.first_landing_url;
      t.first_referrer = ft.first_referrer;
    } else {
      t.first_landing_url = t.current_url;
      t.first_referrer = t.referrer;
      localStorage.setItem(
        KEY,
        JSON.stringify({
          first_landing_url: t.current_url,
          first_referrer: t.referrer,
        })
      );
    }
  } catch (e) {
    /* localStorage 不可環境は無視 */
  }

  // GA4 client_id（_ga Cookie から受動的に取得。値の形式が合わない場合は付与しない）
  try {
    const gaMatch = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (gaMatch) {
      const gaParts = decodeURIComponent(gaMatch[1]).split('.');
      if (gaParts.length === 4) {
        const gaClientId = gaParts[2] + '.' + gaParts[3];
        if (gaClientId.length <= 64 && /^\d+\.\d+$/.test(gaClientId)) {
          t.ga_client_id = gaClientId;
        }
      }
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }

  // クリックID（gclid/gbraid）の Cookie フォールバック。
  // URL に正しい値がある場合はそちらを優先し上書きしない。
  const clickIdPattern = /^[A-Za-z0-9_-]{1,256}$/;
  if (!t.gclid || !clickIdPattern.test(t.gclid)) {
    try {
      const awMatch = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
      if (awMatch) {
        const awParts = decodeURIComponent(awMatch[1]).split('.');
        if (
          awParts.length >= 3 &&
          awParts[0] === 'GCL' &&
          /^\d+$/.test(awParts[1])
        ) {
          const awId = awParts.slice(2).join('.');
          if (clickIdPattern.test(awId)) t.gclid = awId;
        }
      }
    } catch (e) {
      /* Cookie 参照不可環境は無視 */
    }
  }
  if (!t.gbraid || !clickIdPattern.test(t.gbraid)) {
    try {
      const gbMatch = document.cookie.match(/(?:^|;\s*)_gcl_gb=([^;]+)/);
      if (gbMatch) {
        const gbParts = decodeURIComponent(gbMatch[1]).split('.');
        if (
          gbParts.length >= 3 &&
          gbParts[0] === 'GCL' &&
          /^\d+$/.test(gbParts[1])
        ) {
          const gbId = gbParts.slice(2).join('.');
          if (clickIdPattern.test(gbId)) t.gbraid = gbId;
        }
      }
    } catch (e) {
      /* Cookie 参照不可環境は無視 */
    }
  }

  // Meta 広告向け素データ（値の検証のみ行い、そのまま保存する）
  try {
    // eslint-disable-next-line no-control-regex -- Meta fbc の形式検証: 制御文字を明示的に除外する必要がある
    const fbcPattern = /^fb\.\d+\.\d+\.[^\s\x00-\x1F\x7F]+$/;
    const fbcMatch = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
    if (fbcMatch) {
      const fbcVal = decodeURIComponent(fbcMatch[1]);
      if (fbcVal.length <= 512 && fbcPattern.test(fbcVal)) t.fbc = fbcVal;
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }
  try {
    // eslint-disable-next-line no-control-regex -- Meta fbp の形式検証: 制御文字を明示的に除外する必要がある
    const fbpPattern = /^fb\.\d+\.\d+\.[^\s\x00-\x1F\x7F]+$/;
    const fbpMatch = document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/);
    if (fbpMatch) {
      const fbpVal = decodeURIComponent(fbpMatch[1]);
      if (fbpVal.length <= 512 && fbpPattern.test(fbpVal)) t.fbp = fbpVal;
    }
  } catch (e) {
    /* Cookie 参照不可環境は無視 */
  }

  return t;
}

function initPicklesForm(form) {
  const formKey = form.dataset.formKey;
  if (!formKey) return;
  const endpoint = PF_ENDPOINT_BASE + formKey + '/submissions';
  // 冪等キー: フォーム単位で1回だけ生成し、再送でも同じトークンを使う（重複登録・重複通知の防止）。
  const submissionToken = pfUuid();

  function showError(input, fieldEl, controlEl, msg) {
    if (controlEl) controlEl.classList.add('is-error');
    const p = document.createElement('p');
    p.className = 'pf-error';
    p.id = (input.id || 'pf-' + (input.name || 'field')) + '-error';
    p.setAttribute('role', 'alert');
    p.textContent = msg;
    fieldEl.appendChild(p);
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', p.id);
  }
  function clearFieldError(input) {
    const field = input.closest('.pf-field');
    if (field) {
      const e = field.querySelector('.pf-error');
      if (e) e.remove();
    }
    input.classList.remove('is-error');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
    const lbl = input.closest('.pf-check');
    if (lbl) lbl.classList.remove('is-error');
  }
  function fieldMessage(input) {
    if (input.type === 'checkbox') {
      return input.hasAttribute('required') && !input.checked
        ? 'プライバシーポリシーへの同意が必要です。'
        : '';
    }
    if (input.hasAttribute('required') && !input.value.trim()) {
      return 'この必須項目を入力してください。';
    }
    if (
      input.type === 'email' &&
      input.value.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim())
    ) {
      return '正しいメールアドレスを入力してください。';
    }
    return '';
  }
  function validateField(input) {
    clearFieldError(input);
    const msg = fieldMessage(input);
    if (msg) {
      showError(
        input,
        input.closest('.pf-field'),
        input.type === 'checkbox' ? input.closest('.pf-check') : input,
        msg
      );
      return false;
    }
    return true;
  }
  function validateAll() {
    let firstErr = null;
    form.querySelectorAll('.pf-control, input[data-consent]').forEach(input => {
      if (!validateField(input) && !firstErr) firstErr = input;
    });
    if (firstErr) firstErr.focus();
    return !firstErr;
  }

  // blur で即検証 / 入力・変更でエラーが出ている項目を再検証
  form.querySelectorAll('.pf-control, input[data-consent]').forEach(el => {
    const isCheck = el.type === 'checkbox';
    el.addEventListener('blur', () => validateField(el));
    el.addEventListener(isCheck ? 'change' : 'input', () => {
      const field = el.closest('.pf-field');
      if (
        el.classList.contains('is-error') ||
        (field && field.querySelector('.pf-error'))
      ) {
        validateField(el);
      }
    });
  });

  // 条件表示フィールド: data-pf-show-when で指定したコントロールの値が
  // data-pf-show-value のときだけ表示し、その間だけ必須にする。
  // （例: 連絡方法が「お電話」のときだけ電話番号欄を出して必須にする）
  form.querySelectorAll('[data-pf-show-when]').forEach(condEl => {
    const controller = form.querySelector(
      '[name="' + condEl.getAttribute('data-pf-show-when') + '"]'
    );
    if (!controller) return;
    const showValue = condEl.getAttribute('data-pf-show-value');
    const inner = condEl.querySelector('.pf-control');
    const applyConditional = () => {
      const show = controller.value === showValue;
      condEl.hidden = !show;
      if (!inner) return;
      if (show) {
        inner.setAttribute('required', '');
      } else {
        inner.removeAttribute('required');
        inner.value = '';
        clearFieldError(inner);
      }
    };
    applyConditional();
    controller.addEventListener('change', applyConditional);
  });

  // 入力文字にも FONTPLUS を当てる（軽く間引き）
  let fpTimer;
  form.addEventListener('input', () => {
    clearTimeout(fpTimer);
    fpTimer = setTimeout(fpReload, 400);
  });

  form.addEventListener('submit', e => {
    e.preventDefault();
    if (!validateAll()) return;

    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;

    // 送信対象は .pf-control を持つ名前付きコントロール（input/select/textarea）から動的に収集。
    // フォームごとに項目が異なっても対応（ハニーポット website・同意チェックは対象外）。
    // ⚠️ フォーム追加時の注意: 送信したい入力欄には必ず .pf-control を付けること。
    //    付け忘れると name があっても収集対象から静かに漏れる（payload に乗らない）。
    const data = {};
    form.querySelectorAll('.pf-control[name]').forEach(el => {
      // 非表示の条件フィールド（hidden な .pf-field 内）は送信対象から除外する。
      if (el.closest('[hidden]')) return;
      data[el.name] = el.value;
    });
    const hp = form.querySelector('[name="website"]');
    if (hp && hp.value) data.website = hp.value;

    const consent = {
      privacyPolicyAgreed: false,
      personalDataProcessingConsent: false,
      marketingEmailConsent: false,
    };
    form.querySelectorAll('input[data-consent]').forEach(el => {
      const w = el.getAttribute('data-consent');
      if (w === 'privacy') consent.privacyPolicyAgreed = el.checked;
      else if (w === 'personal')
        consent.personalDataProcessingConsent = el.checked;
      else if (w === 'marketing') consent.marketingEmailConsent = el.checked;
    });

    fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        submissionToken,
        data,
        tracking: pfTracking(),
        consent,
      }),
    })
      .then(res => {
        if (res.ok) {
          window.location.href = PF_COMPLETE_URL;
        } else {
          if (btn) btn.disabled = false;
          alert('送信に失敗しました。入力内容をご確認ください。');
        }
      })
      .catch(() => {
        if (btn) btn.disabled = false;
        alert('送信に失敗しました。時間をおいて再度お試しください。');
      });
  });
}

// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  // フォーム初期化（複数フォーム対応）
  document.querySelectorAll('.pf-form[data-form-key]').forEach(initPicklesForm);

  // ロード時フェード（見出し＋本文セクションを同時に）
  const section = document.querySelector('.p-document-dl .l-section');
  const fadeTargets = [section, titleJa].filter(Boolean);
  if (fadeTargets.length) {
    if (REDUCED) {
      gsap.set(fadeTargets, { opacity: 1 });
    } else {
      gsap.to(fadeTargets, {
        opacity: 1,
        ease: 'power3.out',
        duration: 0.8,
        delay: 0.2,
      });
    }
  }
});

// FONTPLUS 初回適用
if (document.readyState === 'complete') {
  fpReload();
} else {
  window.addEventListener('load', fpReload);
}
